import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { from, of, Subject, timer } from 'rxjs';
import { catchError, concatMap, filter, takeUntil, timeout } from 'rxjs/operators';
import { NewVersionSnackbarComponent } from 'src/features/common/cad-app/view/new-version-snackbar/new-version-snackbar.component';

@Injectable()
export class ClientUpdatedService implements OnDestroy {
  private unSub: Subject<void> = new Subject();
  private stopUpdateCheckerSub: Subject<void> = new Subject<void>();
  private readonly versionPollingInterval: number = (environment as any).swVersionPollingIntervalMs ?? 600000; // default 10 minute period
  private readonly suppressReadyToastWindowMs: number = (environment as any).swSuppressReadyToastWindowMs ?? 10000;
  private readonly swDebugLoggingEnabled: boolean = !!(environment as any).swDebugLogging;
  // console.log/info/warn are stubbed out in release builds; set this to 'error' to keep logs in those builds.
  private readonly swDebugLogLevel: 'log' | 'info' | 'warn' | 'error' = (environment as any).swDebugLogLevel ?? 'log';
  private readonly startupTimeMs: number = Date.now();
  private readonly hadSwControllerAtStartup: boolean = !!navigator.serviceWorker?.controller;
  private ignoredInitialUncontrolledReadyEvent: boolean = false;
  private hasPendingUpdate: boolean = false;

  constructor(
    private swUpdate: SwUpdate,
    private matSnackBar: MatSnackBar) {}

  ngOnDestroy(): void {
    this.unSub.next();
    this.unSub.complete();
    this.stopUpdateChecker();
  }

  initNewVersionPolling(): void {
    if (!this.swUpdate.isEnabled) {
      this.log('Service worker updates are disabled for this build.');
      return;
    }

    this.log('Initializing service worker polling.', {
      envName: (environment as any).envName,
      intervalMs: this.versionPollingInterval,
      suppressReadyToastWindowMs: this.suppressReadyToastWindowMs,
      hadSwControllerAtStartup: this.hadSwControllerAtStartup,
    });

    timer(0, this.versionPollingInterval)
      .pipe(
        takeUntil(this.stopUpdateCheckerSub),
        concatMap(() => from(this.swUpdate.checkForUpdate()).pipe(
          timeout(this.versionPollingInterval / 2),
          catchError((error) => {
            // Keep polling after transient network/server issues.
            this.log('checkForUpdate failed for this poll tick. Continuing polling.', error);
            return of(false);
          }))))
      .subscribe((hasUpdate) => {
        this.log('checkForUpdate completed.', { hasUpdate });
      });

    this.swUpdate.versionUpdates
      .pipe(takeUntil(this.unSub))
      .subscribe((event) => {
        this.log('versionUpdates event received.', event);
      });

    this.swUpdate.versionUpdates.pipe(
      takeUntil(this.unSub),
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      filter(() => !this.hasPendingUpdate))
    .subscribe(async (event) => {
      const appUptimeMs = Date.now() - this.startupTimeMs;

      if (appUptimeMs <= this.suppressReadyToastWindowMs) {
        this.log('Suppressing VERSION_READY toast during startup grace window; activating update silently.', {
          appUptimeMs,
          currentHash: event.currentVersion?.hash,
          latestHash: event.latestVersion?.hash,
        });
        await this.activateUpdateSilently('startup-grace-window');
        return;
      }

      if (!this.hadSwControllerAtStartup && !this.ignoredInitialUncontrolledReadyEvent) {
        // If app booted without SW control, the user likely already has fresh network assets.
        // Avoid prompting an immediate redundant refresh toast.
        this.ignoredInitialUncontrolledReadyEvent = true;
        this.log('Ignoring first VERSION_READY because startup had no SW controller; activating update silently.');
        await this.activateUpdateSilently('uncontrolled-startup');
        return;
      }

      this.log('VERSION_READY received. Showing refresh snackbar.');
      this.hasPendingUpdate = true;
      let sbRef =
        this.matSnackBar.openFromComponent(NewVersionSnackbarComponent,{ panelClass:[ 'snackbar-party' ] });
      sbRef.instance.sbRef = sbRef;
    });

    this.swUpdate.unrecoverable
      .pipe(takeUntil(this.unSub))
      .subscribe((event) => {
        this.log('unrecoverable service worker state detected. Reloading page.', event);
        // Force a clean load when the current cached app version becomes unusable.
        window.location.reload();
      });
  }

  async applyUpdateAndReload(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      this.log('applyUpdateAndReload called while updates are disabled. Reloading page.');
      window.location.reload();
      return;
    }

    try {
      this.log('Activating service worker update before reload.');
      await this.swUpdate.activateUpdate();
      this.log('activateUpdate completed successfully.');
    } catch (error) {
      this.log('activateUpdate failed. Reloading anyway.', error);
    } finally {
      this.log('Reloading page to complete update transition.');
      window.location.reload();
    }
  }

  private stopUpdateChecker(): void {
    this.log('Stopping service worker polling stream.');
    this.stopUpdateCheckerSub.next();
    this.stopUpdateCheckerSub.complete();
  }

  private async activateUpdateSilently(reason: string): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      this.log('Silent activateUpdate completed.', { reason });
    } catch (error) {
      this.log('Silent activateUpdate failed.', { reason, error });
    }
  }

  private log(message: string, meta?: any): void {
    if (!this.swDebugLoggingEnabled) {
      return;
    }
    
    const write = console[this.swDebugLogLevel] ?? console.log;
    if (meta === undefined) {
      write('[SW][ClientUpdatedService]', message);
      return;
    }

    write('[SW][ClientUpdatedService]', message, meta);
  }
}
